import { Mutex } from 'async-mutex';
import { assertCanHandle, Resolver } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { ConnectionError, InvalidStateError, TimeoutError } from '#common/types';
import { sleep, Synchronized } from '#common/util';
import { WebFsConnectionLogType } from '#filespace/types';
import { WebSocketAdapter } from '#websocket/client/web-socket';
import { ConnectionState, } from './types/connection';
class ConnectionAbortedError extends Error {
}
export class ConnectionImpl extends Synchronized {
    messageCallbacks;
    reconnectIntervalMs;
    connectTimeoutMs;
    connectionCallbacks;
    endpoint;
    certVerification;
    socket;
    state = ConnectionState.DISCONNECTED;
    log = Logging.getLogger('WebSocketConnection');
    currentConnectResolver = undefined;
    constructor(messageCallbacks, reconnectIntervalMs, connectTimeoutMs, connectionCallbacks) {
        super(new Mutex());
        this.messageCallbacks = messageCallbacks;
        this.reconnectIntervalMs = reconnectIntervalMs;
        this.connectTimeoutMs = connectTimeoutMs;
        this.connectionCallbacks = connectionCallbacks;
    }
    connect(endpoint, certVerification) {
        return this.mutex.runExclusive(async () => {
            if (this.state !== ConnectionState.DISCONNECTED) {
                throw new InvalidStateError('The connection needs to be in a disconnected state in order to initiate a new connect sequence.');
            }
            this.state = ConnectionState.CONNECTING;
            this.endpoint = endpoint;
            this.certVerification = certVerification;
            this.log.info({ logType: WebFsConnectionLogType.CONNECT_START, endpoint: this.getEndpoint() }, 'Initiated connect sequence...');
            let timer;
            const timeout = new Promise((_resolve, reject) => {
                timer = setTimeout(() => {
                    this.onConnectTimeout();
                    reject(new TimeoutError('WebSocket connect timed out'));
                }, this.connectTimeoutMs);
            });
            try {
                return await Promise.race([this.doConnect(), timeout]);
            }
            finally {
                clearTimeout(timer);
            }
        });
    }
    disconnect() {
        this.abortCurrentConnect();
        return this.mutex.runExclusive(() => {
            if (this.state === ConnectionState.DISCONNECTING || this.state === ConnectionState.DISCONNECTED) {
                throw new InvalidStateError('Client is not connected.');
            }
            if (this.state === ConnectionState.RECONNECTING) {
                // The connection has been already closed by the server.
                // No need to close the socket a second time.
                this.state = ConnectionState.DISCONNECTED;
                return;
            }
            this.state = ConnectionState.DISCONNECTING;
            this.log.info({ logType: WebFsConnectionLogType.DISCONNECT_START, endpoint: this.getEndpoint() }, 'Initiated disconnect sequence...');
            return this.doDisconnect();
        });
    }
    getConnectionState() {
        return this.state;
    }
    getEndpoint() {
        return this.endpoint ? this.endpoint : '';
    }
    sendMessage(message) {
        return this.mutex.runExclusive(() => {
            return this.doSendMessage(message);
        });
    }
    connectionState() {
        return this.state;
    }
    static tryGetErrorMessage(event) {
        // in case of an error
        // we would have an WebSocketErrorEvent with a message in node
        // but we would have a bare WebSocketEvent without a message in the browser
        return event?.message ?? '';
    }
    makeConnectError(event) {
        const message = ConnectionImpl.tryGetErrorMessage(event);
        return new ConnectionError(`Connect error to ${this.getEndpoint()}, "${message}"`);
    }
    makeConnectionAbortedError() {
        return new ConnectionAbortedError(`Aborting current connection attempt to ${this.getEndpoint()}`);
    }
    makeDisconnectError(event) {
        const message = ConnectionImpl.tryGetErrorMessage(event);
        return new ConnectionError(`Disconnect error from ${this.getEndpoint()}, "${message}"`);
    }
    makeError(event) {
        const message = ConnectionImpl.tryGetErrorMessage(event);
        return new ConnectionError(`Unknown WebSocket error with ${this.getEndpoint()}, "${message}"`);
    }
    abortCurrentConnect() {
        if (this.currentConnectResolver) {
            this.currentConnectResolver.reject(this.makeConnectionAbortedError());
            this.socket?.close();
        }
    }
    async doConnect() {
        this.currentConnectResolver = new Resolver();
        const onOpen = () => {
            if (!this.currentConnectResolver) {
                return;
            }
            this.onConnectionEstablished();
            this.currentConnectResolver.resolve();
        };
        const onError = (event) => {
            if (!this.currentConnectResolver) {
                return;
            }
            const err = this.onConnectError(event);
            this.currentConnectResolver.reject(err);
        };
        this.socket = new WebSocketAdapter(this.endpoint, this.certVerification);
        this.socket.setOnOpen(onOpen);
        this.socket.setOnError(onError);
        return this.currentConnectResolver.promise.finally(() => {
            this.currentConnectResolver = undefined;
        });
    }
    onConnectionEstablished() {
        this.state = ConnectionState.CONNECTED;
        this.setCallbacksOnConnect();
        this.connectionCallbacks?.onConnected();
        this.log.info({ logType: WebFsConnectionLogType.CONNECT_SUCCESS, endpoint: this.getEndpoint() }, `Connection established.`);
    }
    onConnectError(event) {
        if (this.state !== ConnectionState.RECONNECTING) {
            this.state = ConnectionState.DISCONNECTED;
        }
        const err = this.makeConnectError(event);
        this.log.error({
            logType: WebFsConnectionLogType.CONNECTION_ERROR,
            endpoint: this.getEndpoint(),
            errorMessage: err.message,
        }, 'Connect error');
        return err;
    }
    onConnectTimeout() {
        this.socket.setOnError((_event) => ({}));
        this.socket.close();
        this.socket = undefined;
        this.state = ConnectionState.DISCONNECTED;
        this.log.info({ logType: WebFsConnectionLogType.CONNECT_TIMEOUT, endpoint: this.getEndpoint() }, 'WebSocket connect timed out');
    }
    async tryReconnect() {
        this.log.info({ logType: WebFsConnectionLogType.RECONNECT_START, endpoint: this.getEndpoint() }, 'Starting reconnect sequence...');
        this.state = ConnectionState.RECONNECTING;
        await this.runReconnectLoop();
    }
    async runReconnectLoop() {
        let reconnectAttempts = 0;
        const isConnectionError = (error) => error instanceof ConnectionError;
        while (this.state === ConnectionState.RECONNECTING) {
            try {
                await this.tryReconnectOnce(++reconnectAttempts);
            }
            catch (err) {
                if (err instanceof ConnectionAbortedError) {
                    this.log.warn({
                        logType: WebFsConnectionLogType.RECONNECT_ABORT,
                        endpoint: this.getEndpoint(),
                        retryCounter: reconnectAttempts,
                    }, 'Reconnection aborted');
                    return;
                }
                assertCanHandle(err, isConnectionError);
                this.log.debug({ endpoint: this.getEndpoint() }, `Reconnect failed. Will retry in ${this.reconnectIntervalMs} milliseconds.`);
                await sleep(this.reconnectIntervalMs);
            }
        }
    }
    async tryReconnectOnce(reconnectAttempt) {
        return this.mutex.runExclusive(async () => {
            if (this.state !== ConnectionState.RECONNECTING) {
                return;
            }
            this.log.info({
                logType: WebFsConnectionLogType.RECONNECT_ATTEMPT,
                endpoint: this.getEndpoint(),
                retryCounter: reconnectAttempt,
            }, `Reconnecting...`);
            await this.doConnect();
        });
    }
    doDisconnect() {
        const resolver = new Resolver();
        this.socket.setOnClose(() => {
            this.state = ConnectionState.DISCONNECTED;
            this.connectionCallbacks?.onDisconnected();
            this.log.info({ logType: WebFsConnectionLogType.DISCONNECT_SUCCESS, endpoint: this.getEndpoint() }, 'Successfully disconnected from endpoint.');
            resolver.resolve();
        });
        this.socket.setOnError((event) => resolver.reject(this.makeDisconnectError(event)));
        this.socket.close();
        return resolver.promise;
    }
    doSendMessage(message) {
        const isConnected = this.state === ConnectionState.CONNECTED;
        if (!isConnected) {
            throw new InvalidStateError('Client is not connected');
        }
        return this.socket.send(message);
    }
    setCallbacksOnConnect() {
        this.socket.setOnError((event) => {
            const err = this.makeError(event);
            this.log.error({
                logType: WebFsConnectionLogType.CONNECTION_ERROR,
                endpoint: this.getEndpoint(),
                errorMessage: err.message,
            }, 'Websocket error');
        });
        this.socket.setOnClose(() => {
            this.log.error({ logType: WebFsConnectionLogType.CONNECTION_LOST, endpoint: this.getEndpoint() }, 'Connection lost!');
            this.connectionCallbacks?.onDisconnected();
            if (this.state === ConnectionState.CONNECTED) {
                void this.tryReconnect();
            }
        });
        this.socket.setOnMessage(this.messageCallbacks.onMessageReceived);
    }
}
